import type {
	WhiteboardSmartActions,
	ToggleWhiteboardAIAgentRightPanel,
	SmartActionsUpdateHandler,
	ToggleAIAgentDialog,
	MountPosition,
	WhiteboardAIAgentServiceInterface,
	UpdateAIAgentDialogState,
	AIAgentDialogState,
	UpdateConfluenceSidebarState,
	ConfluenceSidebarState,
} from '@confluence/whiteboard-types/entry-points/WhiteboardAIAgentService';
import { fg } from '@confluence/feature-gating';

export const DEFAULT_MOUNT_POSITION: MountPosition = {
	toggleBottom: -1,
	toggleMiddle: -1,
};

export class WhiteboardAIAgentService implements WhiteboardAIAgentServiceInterface {
	private handleToggleWhiteboardAIAgentRightPanel: ToggleWhiteboardAIAgentRightPanel;
	private handleSmartActionsUpdate: SmartActionsUpdateHandler;
	private handleToggleAIAgentDialog: ToggleAIAgentDialog;
	private handleUpdateAIAgentDialogState: UpdateAIAgentDialogState;
	private handleConfluenceSidebarStateChange: UpdateConfluenceSidebarState[];
	private agentEnablementChangeHandlers: ((isAgentEnabled: boolean) => void)[];
	private whiteboardContainerRef: React.RefObject<HTMLDivElement> | null;
	private aiAgentButtonRef: React.RefObject<HTMLDivElement> | null;
	private isAgentEnabled: boolean;

	constructor() {
		this.handleToggleWhiteboardAIAgentRightPanel = () => {};
		this.handleSmartActionsUpdate = () => [];
		this.handleToggleAIAgentDialog = () => {};
		this.handleUpdateAIAgentDialogState = () => {};
		this.handleConfluenceSidebarStateChange = [];
		this.agentEnablementChangeHandlers = [];
		this.whiteboardContainerRef = null;
		this.aiAgentButtonRef = null;
		this.isAgentEnabled = false;
	}

	dispose(): void {
		this.handleToggleWhiteboardAIAgentRightPanel = () => {};
		this.handleSmartActionsUpdate = () => [];
		this.handleToggleAIAgentDialog = () => {};
		this.handleUpdateAIAgentDialogState = () => {};
		this.handleConfluenceSidebarStateChange = [];
		this.agentEnablementChangeHandlers = [];
		this.whiteboardContainerRef = null;
		this.aiAgentButtonRef = null;
		this.isAgentEnabled = false;
	}

	/**
	 * Returns the current read only state
	 */
	getIsAgentEnabled(): boolean {
		return this.isAgentEnabled;
	}

	/**
	 * Local Confluence handler register for listening on permission changes for whiteboard ai agent
	 */
	onAgentEnablementChange(callback: (isAgentEnabled: boolean) => void) {
		this.agentEnablementChangeHandlers.push(callback);
	}

	/**
	 * Notify permission change handlers of updates
	 */
	updatePermissions(permissions: string[]) {
		this.isAgentEnabled =
			permissions.includes('write') && fg('cc-ai-whiteboards-enable-digital-teammate');
		this.agentEnablementChangeHandlers.forEach((handler) => handler(this.isAgentEnabled));
	}

	/**
	 * Registers a callback in whiteboards to be called when the whiteboard ai agent right panel is toggled
	 */
	registerToggleWhiteboardAIAgentRightPanel(callback: ToggleWhiteboardAIAgentRightPanel) {
		this.handleToggleWhiteboardAIAgentRightPanel = callback;
	}

	/**
	 * Allows for a callback to be registered in whiteboards returning the currently available smart ai actions list
	 */
	registerOnSmartActionsUpdate(callback: SmartActionsUpdateHandler) {
		this.handleSmartActionsUpdate = callback;
	}

	/**
	 * Allows for a callback to be registered in whiteboards to be called when the ai agent dialog is toggled
	 */
	registerAIAgentToggleHandler(callback: ToggleAIAgentDialog) {
		this.handleToggleAIAgentDialog = callback;
	}

	/**
	 * Calls registered handler for whiteboard closeWhiteboardAIAgentRightPanel bridge action
	 * Registration found in useWhiteboardAIAgentService
	 */
	toggleWhiteboardAIAgentRightPanel() {
		this.handleToggleWhiteboardAIAgentRightPanel();
	}

	/**
	 * Calls callback registered in whiteboards to retrieve currently available smart ai actions
	 */
	updateSmartActions(availableActions: string[]) {
		return this.handleSmartActionsUpdate(availableActions);
	}

	/**
	 * Calls handler registered in canvas to open the internal ai agent dialog
	 */
	toggleAIAgentDialog(properties?: { selectedAction?: WhiteboardSmartActions }) {
		this.handleToggleAIAgentDialog(properties);
	}

	/**
	 * React ref objects required to calculate the current mount position
	 */
	setWhiteboardContainerRef(ref: React.RefObject<HTMLDivElement>) {
		this.whiteboardContainerRef = ref;
	}

	setAIAgentButtonRef(ref: React.RefObject<HTMLDivElement>) {
		this.aiAgentButtonRef = ref;
	}

	/**
	 * Calls handler registered in canvas to return a mount point for the ai agent dialog
	 */
	getAIAgentDialogMountPoint(): MountPosition {
		if (!this.whiteboardContainerRef?.current || !this.aiAgentButtonRef?.current) {
			return DEFAULT_MOUNT_POSITION;
		}

		const containerRect = this.whiteboardContainerRef.current.getBoundingClientRect();
		const buttonRect = this.aiAgentButtonRef.current.getBoundingClientRect();

		// toggleMiddle is calculated as the offset from the right edge of the container as
		// that is where we want the dialog to be anchored
		const toggleMiddle = containerRect.right - (buttonRect.left + buttonRect.width / 2);
		const toggleBottom = buttonRect.bottom - containerRect.top;

		return {
			toggleBottom,
			toggleMiddle,
		};
	}

	/**
	 * Calls a handler meant to be registered in Confluence to update the ai agent dialog state
	 */
	onUpdateAIAgentDialogState(callback: UpdateAIAgentDialogState) {
		this.handleUpdateAIAgentDialogState = callback;
	}

	/**
	 * Updates the ai agent dialog state
	 */
	updateAIAgentDialogState(state: AIAgentDialogState) {
		this.handleUpdateAIAgentDialogState(state);
	}

	/**
	 * Calls a handler meant to be registered in Confluence to update the ai agent dialog state
	 */
	onConfluenceSidebarStateChange(callback: UpdateConfluenceSidebarState) {
		this.handleConfluenceSidebarStateChange.push(callback);
	}

	/**
	 * Calls a handler meant to be registered in Whiteboards to notify of changes in the Confluence sidebar state
	 */
	updateConfluenceSidebarState(state: ConfluenceSidebarState) {
		this.handleConfluenceSidebarStateChange.forEach((callback) => callback(state));
	}
}

export const whiteboardAIAgentService = new WhiteboardAIAgentService();
